import { colorFor } from '../lib/definitions/palette';
import { trustTierInfo } from '../lib/definitions/trust';
import { useAppData } from "../state/AppData";

// Observer-provenance chip, reused everywhere a packet, node, or list row
// needs to say "who reported this, how trusted are they" (spec §4, item 4 of
// the phase-2 brief): the packet feed, packet detail, node detail's
// heard-by list, and the node list's Observers column all import this one
// component so the treatment is identical everywhere, not re-invented per
// screen. `trustTier` can be passed directly (a packet row already carries
// one); when omitted, it's looked up from the real /api/observers registry
// via AppData (falling back to /api/stats' observers list — see
// AppData.tsx's observerTrust). The friendly label (e.g. "CoreScope") comes
// from that same registry when available; falls back to the bare id — or
// from the optional `label` override below, when a caller's own response
// already carries a more specific/authoritative id->label mapping (e.g.
// coverage's own `observers` array) than the app-wide registry.
//
// `compact` hides the label entirely, showing only the tier-coloured dot
// and the short tier text — correct for a dense multi-badge cell where the
// tier alone is the point (e.g. a packet row). Correction:
// CoveragePage was passing `compact` for "seen by"/"missing
// from"/"last seen by", which meant every one of those cells rendered only
// the trust TIER ("External (unverified)") with the actual observer name
// nowhere on screen — answering a question nobody asked and hiding the one
// they did. Non-compact is the right mode whenever the observer's identity
// is itself the useful fact, not just its trust tier.
export function ObserverBadge({
  observerId,
  trustTier,
  label: labelOverride,
  compact,
}: {
  observerId: string;
  trustTier?: string | null;
  label?: string | null;
  compact?: boolean;
}) {
  const { effectiveTheme, observerTrust, observers } = useAppData();
  const tier = trustTier ?? observerTrust.get(observerId) ?? null;
  const info = trustTierInfo(tier);
  const label = labelOverride || observers.find((o) => o.id === observerId)?.label || observerId;

  return (
    <span
      className="observer-badge"
      data-verified={info.verified}
      title={`${label} (${observerId}) — ${info.label}`}
      style={{ borderColor: colorFor(info.color, effectiveTheme) }}
    >
      <span className="observer-badge-dot" style={{ background: colorFor(info.color, effectiveTheme) }} />
      {!compact && <span className="observer-badge-id">{label}</span>}
      <span className="observer-badge-tier">{info.short}</span>
    </span>
  );
}
